<i18n lang="yaml">
en:
  heading: RVs for sale
  noResultsFound: No results found
  noResultsFoundTip: Try changing location or search criteria

fr:
  heading: VR à vendre
  noResultsFound: Aucun résultat trouvé
  noResultsFoundTip: Essayez de changer de lieu ou de critères de recherche
</i18n>

<i18n lang="yaml" src="~/i18n/rv-type.yml"></i18n>
<i18n lang="yaml" src="~/i18n/sort.yml"></i18n>

<template>
  <div class="search">
    <div ref="resultsWrapEl" class="results-wrap">
      <template v-if="rvs.length">
        <Breadcrumbs v-if="breadcrumb?.length" :items="breadcrumb" class="mt-4" />

        <div class="flex items-center justify-between gap-4 mt-4">
          <h1 class="flex-shrink text-3xl">{{ title ?? t('heading') }}</h1>
          <SearchSort v-if="!isLargeScreen" />
        </div>

        <div class="results mt-4">
          <RvCard
            v-for="(rv, index) in rvs"
            :key="index"
            :rv="rv"
            :index="index"
            :cta="cta"
            :page-source="pageSource"
          />
        </div>

        <SearchPagination :page-size="pageSize" :total-results="totalResults" />
      </template>
      <div v-else class="no-results-found mt-4">
        <div class="content">
          <img loading="lazy" :src="`${staticCDN}/web/images/no-results-found.svg`" alt="" />
          <h1>{{ t('noResultsFound') }}</h1>
          <p>{{ t('noResultsFoundTip') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useScroll } from '@vueuse/core'

import { SegmentEvents } from '~/enums/segment-events'
import { getTypesFromFilterParams } from '~/lib/search'
import { segmentTrack } from '~/lib/tracking'
import type { ListingConditions, Rv, RvCategories } from '~/types/api'
import type { IGooglePlace } from '~/types/common/google-place'
import type { MenuItem } from '~/types/components/menu-item'
import type { ISearchFilters } from '~/types/search/filters'
import type { ISearchParameters } from '~/types/search/search'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    title?: string | null
    rvs: Rv[]
    totalResults: number
    pageSource: string
    cta: string
    pageSize: number
    navigatablePages?: boolean
    currentPage: number
    searchParameters: ISearchParameters
    breadcrumb?: MenuItem[]
  }>(),
  {
    rvs: () => [],
    totalResults: 0,
    pageSize: 30
  }
)

const staticCDN = useRuntimeConfig().public.app.staticCdnEndpoint

const isLargeScreen = useIsLargeScreen()

const resultsWrapEl = ref<HTMLDivElement | null>(null)
const { y: resultsWrapScroll } = useScroll(resultsWrapEl)

const arrayUtils = useArrayUtils()

onMounted(() => {
  trackSearchViewed()
})

const trackSearchViewed = () => {
  const sp = props.searchParameters ?? {}
  const location = sp.location ?? ({} as IGooglePlace)
  const filters = sp.filters ?? ({} as ISearchFilters)

  segmentTrack(SegmentEvents.SearchViewed, {
    numResults: props.totalResults,
    numResultsDisplayed: props.rvs.length,
    searchCountry: location.country?.long_name,
    searchRegion: location.region?.long_name,
    searchCity: location.city?.long_name,
    searchAddress: location.fullName,
    rvTypes: getTypesFromFilterParams(filters),
    listingType: arrayUtils.distinct<RvCategories>(props.rvs.map((r) => r.category)),
    rvManufacturer: filters.rvBrand,
    priceMin: filters.rvPrice?.min,
    priceMax: filters.rvPrice?.max,
    minLength: filters.rvLength?.min,
    maxLength: filters.rvLength?.max,
    minWeight: filters.rvWeight?.min,
    maxWeight: filters.rvWeight?.max,
    minRvYear: filters.rvYear?.min,
    maxRvYear: filters.rvYear?.max,
    listingCondition: arrayUtils.getArrayFromRecord<ListingConditions>(filters.listingCondition),
    sortOrder: sp.sort,
    searchPage: props.currentPage
  })
}

watch(
  () => props.currentPage,
  () => {
    resultsWrapScroll.value = 0
    trackSearchViewed()
  }
)

watch(
  () => props.totalResults,
  () => {
    trackSearchViewed()
    resultsWrapScroll.value = 0
  },
  { deep: true }
)
</script>

<style lang="scss" scoped>
$headerHeightDesktop: 158px;
$headerHeightMobile: 128px;

.search {
  display: grid;

  .results-wrap {
    padding: 0 1rem;

    .title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      grid-gap: 1rem;

      h1 {
        flex-shrink: 0;
      }
    }
  }

  .results {
    display: grid;
    grid-gap: 2rem 1rem;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: 1024px) {
      grid-template-columns: repeat(4, 1fr);
    }

    @media (min-width: 1280px) {
      grid-template-columns: repeat(5, 1fr);
    }
  }

  .no-results-found {
    .content {
      margin-top: 2rem;
      text-align: center;

      h1 {
        margin-top: 1.25rem;
        margin-bottom: 1rem;
        color: $primary-500;
        text-transform: capitalize;
      }

      p {
        font-weight: 400;
        font-style: normal;
        font-size: 1.5rem;
        line-height: 2.5rem;
      }
    }
  }
}

.show-listing {
  position: fixed;
  z-index: 1;
  width: 100%;
  bottom: 0;
  background-color: #fff;
  border: 0;
  padding: 0.5rem 1.25rem;
  font-size: 1rem;
  line-height: 1.5rem;
  font-weight: 600;
  color: $primary-500;
  border-radius: 2rem 2rem 0 0;
}
</style>
